import React, { useState } from 'react';
import { startCheckout, openBillingPortal } from './api';
import { useTranslation } from '../i18n';
import LangToggle from './LangToggle';

const Symbol = ({ size = 28 }) => (
  <svg width={size} height={size} viewBox="0 0 80 80" fill="none" aria-hidden="true" focusable="false">
    <polyline points="16,64 16,20 40,46 64,20 64,64" stroke="#14653a" strokeWidth="6.5" strokeLinecap="round" strokeLinejoin="round" fill="none" strokeDasharray="0.1 11" opacity="0.55"/>
    <circle cx="16" cy="64" r="5" fill="#14653a" opacity="0.55"/>
    <circle cx="40" cy="46" r="4" fill="#14653a" opacity="0.55"/>
    <circle cx="64" cy="64" r="4" fill="#14653a" opacity="0.55"/>
    <path d="M55.9,20.5 L64,35.35 L72.1,20.5 Z" fill="#14653a"/>
    <circle cx="64" cy="16" r="9" fill="#14653a"/>
    <circle cx="64" cy="16" r="3.6" fill="#ffffff"/>
  </svg>
);

export default function Billing({ business, onSignOut }) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(null); // 'monthly' | 'yearly' | 'portal' | null
  const [error, setError] = useState('');

  const status = business?.subscription_status;
  const hasStripeCustomer = !!business?.stripe_customer_id;

  // Pick the right headline for the moment the user is seeing this.
  let title = t('billing.title_default');
  let subtitle = t('billing.subtitle_default');
  if (status === 'past_due') {
    title = t('billing.title_past_due');
    subtitle = t('billing.subtitle_past_due');
  } else if (status === 'canceled') {
    title = t('billing.title_canceled');
    subtitle = t('billing.subtitle_canceled');
  } else if (status === 'trial' && business?.trial_ends_at && new Date(business.trial_ends_at) < new Date()) {
    title = t('billing.title_expired');
    subtitle = t('billing.subtitle_expired');
  }

  const subscribe = async (plan) => {
    if (loading !== null) return;
    setError('');
    setLoading(plan);
    try {
      const { data } = await startCheckout(plan);
      if (data?.url) {
        window.location.href = data.url;
        return;
      }
      throw new Error(t('billing.no_checkout_url'));
    } catch (err) {
      setError(err.response?.data?.error || err.message || t('billing.checkout_error'));
      setLoading(null);
    }
  };

  const openPortal = async () => {
    if (loading !== null) return;
    setError('');
    setLoading('portal');
    try {
      const { data } = await openBillingPortal();
      if (data?.url) {
        window.location.href = data.url;
        return;
      }
      throw new Error(t('billing.no_portal_url'));
    } catch (err) {
      setError(err.response?.data?.error || err.message || t('billing.portal_error'));
      setLoading(null);
    }
  };

  // Each plan card fires checkout on activation, so it's a button (an action),
  // not a radio (a persistent selection). The label spells out the action.
  const planLabel = (plan) =>
    `${t('billing.start_free_trial')} — ${plan === 'monthly' ? t('billing.monthly') : t('billing.yearly')}`;

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <div style={styles.logo}>
          <Symbol size={28} />
          <span style={styles.logoText}>
            <span style={{ color: '#14653a' }}>Mow</span><span style={{ color: '#14653a' }}>Stop</span>
          </span>
        </div>
        <h1 style={styles.title}>{title}</h1>
        <div style={styles.subtitle}>{subtitle}</div>

        {error && (
          <div style={styles.errorBox} role="alert">
            {error}
          </div>
        )}

        <div style={styles.plans} role="group" aria-label={t('a11y.select_plan')}>
          <button
            type="button"
            style={{ ...styles.plan, ...styles.planMonthly, ...(loading === 'monthly' ? styles.planActive : {}) }}
            onClick={() => subscribe('monthly')}
            disabled={loading !== null}
            aria-label={planLabel('monthly')}
            aria-busy={loading === 'monthly'}
          >
            <div style={styles.planName}>{t('billing.monthly')}</div>
            <div style={styles.planPrice}>$39<span style={styles.planPer}>{t('billing.per_mo')}</span></div>
            <div style={styles.planNote}>{t('billing.billed_monthly')}</div>
            <div style={styles.planCta} aria-hidden="true">
              {loading === 'monthly' ? t('app.opening_dots') : t('billing.start_free_trial')}
            </div>
          </button>

          <button
            type="button"
            style={{ ...styles.plan, ...styles.planYearly, ...(loading === 'yearly' ? styles.planActive : {}) }}
            onClick={() => subscribe('yearly')}
            disabled={loading !== null}
            aria-label={planLabel('yearly')}
            aria-busy={loading === 'yearly'}
          >
            <div style={styles.bestValue} aria-hidden="true">{t('billing.save_109')}</div>
            <div style={styles.planName}>{t('billing.yearly')}</div>
            <div style={styles.planPrice}>$359<span style={styles.planPer}>{t('billing.per_yr')}</span></div>
            <div style={styles.planNote}>{t('billing.yearly_note')}</div>
            <div style={styles.planCta} aria-hidden="true">
              {loading === 'yearly' ? t('app.opening_dots') : t('billing.start_free_trial')}
            </div>
          </button>
        </div>

        <div style={styles.trialNote}>{t('billing.trial_perks')}</div>

        {hasStripeCustomer && (
          <button
            type="button"
            style={styles.portalBtn}
            disabled={loading !== null}
            onClick={openPortal}
          >
            {loading === 'portal' ? t('app.opening_dots') : t('billing.already_customer_manage')}
          </button>
        )}

        <div style={{ display: 'flex', justifyContent: 'center', marginTop: 8, marginBottom: 8 }}>
          <LangToggle />
        </div>

        <button type="button" style={styles.signOutBtn} onClick={onSignOut}>
          {t('app.sign_out')}
        </button>
      </div>
    </div>
  );
}

const styles = {
  page: { minHeight: '100vh', background: '#f3f4ef', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' },
  card: { background: '#fff', borderRadius: 20, padding: 36, width: '100%', maxWidth: 460, border: '1px solid #e3e9df', boxShadow: '0 30px 80px rgba(20, 101, 58, 0.08)' },
  logo: { display: 'flex', alignItems: 'center', gap: 10, justifyContent: 'center', marginBottom: 16 },
  logoText: { fontSize: 22, fontWeight: 800, letterSpacing: '-0.5px', fontFamily: 'Manrope, -apple-system, BlinkMacSystemFont, sans-serif' },
  title: { textAlign: 'center', fontSize: 23, fontWeight: 800, color: '#1a1a1a', marginBottom: 6, marginTop: 0, fontFamily: 'Manrope, -apple-system, sans-serif', letterSpacing: '-0.4px' },
  subtitle: { textAlign: 'center', fontSize: 13.5, color: '#5f6b58', marginBottom: 24 },
  errorBox: { background: '#FEF2F2', color: '#dc2626', borderRadius: 8, padding: '10px 12px', fontSize: 13, marginBottom: 16 },
  plans: { display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 16 },
  plan: { position: 'relative', display: 'block', width: '100%', font: 'inherit', appearance: 'none', WebkitAppearance: 'none', textAlign: 'left', padding: '18px 20px', border: '1px solid #e3e9df', borderRadius: 14, background: '#fff', cursor: 'pointer', transition: 'border-color .15s ease, box-shadow .15s ease' },
  planMonthly: {},
  planYearly: { borderColor: '#14653a', boxShadow: '0 2px 10px rgba(20, 101, 58, .10)' },
  planActive: { boxShadow: '0 0 0 2px #14653a' },
  planName: { fontSize: 13, fontWeight: 700, color: '#5f6b58', marginBottom: 4, letterSpacing: '0.01em' },
  planPrice: { fontSize: 28, fontWeight: 800, color: '#1a1a1a', letterSpacing: '-.5px', fontFamily: 'Manrope, -apple-system, sans-serif' },
  planPer: { fontSize: 14, fontWeight: 600, color: '#5f6b58' },
  planNote: { fontSize: 12, color: '#5f6b58', marginTop: 4, marginBottom: 14 },
  planCta: { background: '#14653a', color: '#fff', borderRadius: 8, padding: '10px 12px', fontSize: 13.5, fontWeight: 700, textAlign: 'center' },
  bestValue: { position: 'absolute', top: -10, right: 14, background: '#14653a', color: '#fff', fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 100 },
  trialNote: { textAlign: 'center', fontSize: 12, color: '#14653a', marginBottom: 18, fontWeight: 600 },
  portalBtn: { width: '100%', padding: 12, border: '1px solid #e3e9df', background: '#fff', borderRadius: 10, fontSize: 13, color: '#14653a', fontWeight: 700, cursor: 'pointer', marginBottom: 10 },
  signOutBtn: { width: '100%', padding: 10, border: 'none', background: 'none', fontSize: 12, color: '#5f6b58', cursor: 'pointer' }
};
